import React, {Component} from 'react'
import moment from 'moment'
import {createSelector} from 'reselect'
import reduce from 'lyjs/es/fp/reduce'
import toObject from 'lyjs/es/fp/toObject'
import toArray from 'lyjs/es/fp/toArray'
import map from 'lyjs/es/fp/map'
import {Helmet} from 'react-helmet'

import createStateSelector from 'services/utils/createStateSelector'
import ossApi from 'services/api/oss'
import apiState from './components/apiState'
import RoisTimelineChart from './components/RoisTimelineChart'
import RoisDistributionChart from './components/RoisDistributionChart'
import StatsTimelineChart from './components/StatsTimelineChart'
import FeatsDistributionChart from './components/FeatsDistributionChart'
import FeatsTimelineChart from './components/FeatsTimelineChart'
import TraceTimelineChart from './components/TraceTimelineChart'
import './style.css'

const pendingSelector = createStateSelector('pending', false)
const dataSelector = createStateSelector('data', [])

const RoisState = apiState({
  fetch(uuid){
    return ossApi.fetchRois(uuid)
  },
  effects(setState) {
    return {
      updateIndex(index) {
        setState({index})
      }
    }
  },
  selector: createSelector(
    pendingSelector,
    createStateSelector('index'),
    createSelector(
      dataSelector,
      (data) => {
        return reduce((result, x) => {
          const {
            timestamp,
            aa_make_trade_num = 0,
            aa_make_trade_fee = 0,
            ah_make_trade_num = 0,
            ah_make_trade_fee = 0,
            rt_make_trade_num = 0,
            rt_make_trade_fee = 0
          } = x

          const time = moment(timestamp).format('YYYY-MM-DD')

          result.times.push(time)
          result.robotTrades.push([time, ah_make_trade_num + rt_make_trade_num])
          result.robotSales.push([time, (ah_make_trade_fee + rt_make_trade_fee) / 1e4])
          result.pureAssistantTrades.push([time, aa_make_trade_num])
          result.pureAssistantSales.push([time, aa_make_trade_fee / 1e4])
          result.tradeAssist.push([time, ah_make_trade_num * 100 / aa_make_trade_num])
          result.salesAssist.push([time, ah_make_trade_fee * 100 / aa_make_trade_fee])
          result.trades.push([
            {name: '全自动成单', value: rt_make_trade_num},
            {name: '人机半自动成单', value: ah_make_trade_num},
            {name: '纯手工成单', value: aa_make_trade_num}
          ])
          result.sales.push([
            {name: '全自动成单', value: rt_make_trade_fee},
            {name: '人机半自动成单', value: ah_make_trade_fee},
            {name: '纯手工成单', value: aa_make_trade_fee}
          ])

          return result
        }, {
          times: [],
          robotTrades: [],
          robotSales: [],
          pureAssistantTrades: [],
          pureAssistantSales: [],
          tradeAssist: [],
          salesAssist: [],
          trades: [],
          sales: []
        }, data)
      }
    ),
    (loading, index, data) => ({
      loading,
      index: index === undefined ? 3 : index,
      ...data
    })
  )
})

const StatsState = apiState({
  fetch(uuid) {
    return ossApi.fetchStats(uuid)
  },
  selector: createSelector(
    pendingSelector,
    createSelector(
      dataSelector,
      (data) => {
        return reduce((result, x) => {
          const {
            timestamp,
            questions,
            dialogues,
            customers,
            covered_questions_percent,
            covered_dialogue_percent
          } = x

          const time = moment(timestamp).format('YYYY-MM-DD')

          result.times.push(time)
          result.questions.push([time, questions / 1e4])
          result.dialogues.push([time, dialogues / 1e4])
          result.customers.push([time, customers / 1e4])
          result.questionsCoverages.push([time, covered_questions_percent * 100])
          result.dialoguesCoverages.push([time, covered_dialogue_percent * 100])

          return result
        }, {
          times: [],
          questions: [],
          dialogues: [],
          customers: [],
          questionsCoverages: [],
          dialoguesCoverages: []
        }, data)
      }
    ),
    (loading, data) => ({
      loading,
      ...data
    })
  )
})

const FeatsDistributionState = apiState({
  fetch(uuid){
    return ossApi.fetchFeats(uuid)
  },
  selector: createSelector(
    pendingSelector,
    dataSelector,
    (loading, dataSource) => ({
      loading,
      dataSource
    })
  )
})

const FeatsTimelineState = apiState({
  fetch(uuid) {
    return ossApi.fetchFeatsTimeline(uuid)
  },
  selector: createSelector(
    pendingSelector,
    createSelector(
      dataSelector,
      (data) => {
        return reduce((result, x) => {
          const {
            name,
            count,
            data
          } = x

          result.dataSource.push({
            name,
            count,
            data: data.map((record) => {
              const {
                timestamp,
                value
              } = record

              const time = moment(timestamp).format('YYYY-MM-DD HH')

              if (result.times.indexOf(time) < 0) {
                result.times.push(time)
              }

              return [time, value, count]
            })
          })

          return result
        }, {
          times: [],
          dataSource: []
        }, data)
      }
    ),
    (loading, data) => ({
      loading,
      ...data
    })
  )
})

const TraceState = apiState({
  fetch(uuid) {
    return ossApi.fetchTrace(uuid)
  },
  selector: createSelector(
    (state, props) => props.date,
    pendingSelector,
    createSelector(
      dataSelector,
      (data) => {
        return toObject(map((x) => {
          const {
            timestamp,
            nTrace,
            rTrace
          } = x

          const date = moment(timestamp).format('YYYY-MM-DD')
          const value = reduce((result, x) => {
            const {
              timestamp,
              questions,
              make_trade_num
            } = x

            const time = moment(timestamp).format('YYYY-MM-DD HH:mm:SS')
            const r = rTrace.find((t) => t.timestamp === timestamp) || {}

            result.times.push(time)
            result.nQuestions.push([time, questions / 1e4])
            result.rReplies.push([time, r.replies / 1e4 || 0])
            result.rates.push([time, (r.make_trade_num * 100 / make_trade_num) || 0])

            return result
          }, {
            times: [],
            nQuestions: [],
            rReplies: [],
            rates: []
          }, nTrace)

          return [date, value]
        }), data)
      }
    ),
    (date, loading, entries) => ({
      loading,
      ...entries[date]
    })
  )
})

class Main extends Component {
  render() {
    const {
      props: {
        match: {
          params: {
            uuid,
            title
          }
        }
      }
    } = this

    return (
      <div className="main" >
        <Helmet>
          <title>{`${title} - 双十二战报`}</title>
        </Helmet>
        <h3 className="text-center mt-3 mb-3" >
          {title}
        </h3>
        <RoisState uuid={uuid}>
          {({loading, index, times, robotTrades, robotSales, pureAssistantTrades, pureAssistantSales, tradeAssist, salesAssist, trades, sales, updateIndex}) => (
            <div>
              <RoisTimelineChart
                loading={loading}
                option={{
                  title: {
                    text: '双十二订单量趋势'
                  },
                  series: [{
                    name: '机器人参与',
                    type: 'bar',
                    stack: 'trade',
                    data: robotTrades
                  }, {
                    name: '纯人工',
                    type: 'bar',
                    stack: 'trade',
                    data: pureAssistantTrades
                  }, {
                    name: '助攻率',
                    type: 'line',
                    yAxisIndex: 1,
                    data: tradeAssist
                  }]
                }}
                xAxisData={times}
                yAxisNames={['订单量(单)', '助攻率(%)']}
                onEvents={{
                  click: (params) => updateIndex(params.dataIndex)
                }}
              />
              <RoisTimelineChart
                loading={loading}
                option={{
                  title: {
                    text: '双十二订单金额趋势'
                  },
                  series: [{
                    name: '机器人参与',
                    type: 'bar',
                    stack: 'sale',
                    data: robotSales
                  }, {
                    name: '纯人工',
                    type: 'bar',
                    stack: 'sale',
                    data: pureAssistantSales
                  }, {
                    name: '助攻率',
                    type: 'line',
                    yAxisIndex: 1,
                    data: salesAssist
                  }]
                }}
                xAxisData={times}
                yAxisNames={['订单金额(万)', '助攻率(%)']}
                onEvents={{
                  click: (params) => updateIndex(params.dataIndex)
                }}
              />
              {
                times[index] && (
                  <div className="row" >
                    <div className="col-12 col-md-6">
                      <RoisDistributionChart
                        loading={loading}
                        option={{
                          title: {
                            text: `${moment(times[index]).format('MM月DD日')}订单量分布`
                          },
                          legend: {
                            data: toArray(map((x) => x.name), trades[index])
                          },
                          series: {
                            data: trades[index]
                          }
                        }}
                      />
                    </div>
                    <div className="col-12 col-md-6">
                      <RoisDistributionChart
                        loading={loading}
                        option={{
                          title: {
                            text: `${moment(times[index]).format('MM月DD日')}订单金额分布`
                          },
                          legend: {
                            data: toArray(map((x) => x.name), sales[index])
                          },
                          series: {
                            data: sales[index]
                          }
                        }}
                      />
                    </div>
                    <div className="col-12" >
                      <TraceState uuid={uuid} date={times[index]} >
                        {({loading, date, times = [], nQuestions = [], rates = []}) => (
                          <TraceTimelineChart
                            loading={loading}
                            option={{
                              title: {
                                text: `${moment(date).format('MM月DD日')}忙碌趋势分布`
                              },
                              series: [{
                                name: '店铺问题总数',
                                type: 'bar',
                                data: nQuestions
                              }, {
                                name: '机器人成单占比',
                                type: 'line',
                                yAxisIndex: 1,
                                data: rates
                              }]
                            }}
                            xAxisData={times}
                          />
                        )}
                      </TraceState>
                    </div>
                  </div>
                )
              }
            </div>
          )}
        </RoisState>
        <StatsState uuid={uuid} >
          {({loading, times, questions, dialogues, customers, questionsCoverages, dialoguesCoverages}) => (
            <StatsTimelineChart
              loading={loading}
              option={{
                title: {
                  text: '双十二工作量趋势'
                },
                series: [{
                  name: '机器人接收问题数',
                  type: 'bar',
                  data: questions
                }, {
                  name: '问题覆盖率',
                  type: 'line',
                  yAxisIndex: 1,
                  data: questionsCoverages
                }, {
                  name: '对话数',
                  type: 'bar',
                  data: dialogues
                }, {
                  name: '对话覆盖率',
                  type: 'line',
                  yAxisIndex: 1,
                  data: dialoguesCoverages,
                }, {
                  name: '服务顾客数',
                  type: 'bar',
                  data: customers
                }]
              }}
              xAxisData={times}
            />
          )}
        </StatsState>
        <FeatsDistributionState uuid={uuid} >
          {({loading, dataSource}) => (
            <FeatsDistributionChart
              loading={loading}
              option={{
                title: {
                  text: '双十二顾客咨询热门问题分布',
                },
                series: {
                  data: dataSource
                }
              }}
            />
          )}
        </FeatsDistributionState>
        <FeatsTimelineState uuid={uuid} >
          {({loading, times, dataSource}) => (
            <FeatsTimelineChart
              loading={loading}
              option={{
                title: {
                  text: '12月12日顾客咨询热门问题分布趋势'
                },
                series: toArray(map((x) => {
                  return {
                    ...x,
                    type: 'bar',
                    stack: 'feat'
                  }
                }),dataSource)
              }}
              xAxisData={times}
            />
          )}
        </FeatsTimelineState>
      </div>
    )
  }
}

export default Main



// WEBPACK FOOTER //
// ./src/scenes/Main/Main.js